import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { RefreshCw, Search, Server } from "lucide-react"
import { smartoltService, type SmartOLTOltSettings } from "../../../services/smartolt.service.ts"
import { toast } from "../../../utils/notify.ts"
import { SMARTOLT_PATHS } from "../smartoltNav.ts"
import { filterRows, formatUpdated } from "./smartoltSettingsUtils.ts"

const empty: SmartOLTOltSettings = { items: [], summary: { total: 0, reachable: 0, stale: 0 }, updated_at: null }

export function SmartOLTOltSettingsPage({
  title = "OLT Settings",
  subtitle = "Optical line terminals provisioned and managed through SmartOLT",
}: {
  title?: string
  subtitle?: string
} = {}) {
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [data, setData] = useState<SmartOLTOltSettings>(empty)
  const [search, setSearch] = useState("")

  const load = async (silent = false, sync = false) => {
    if (!silent) setLoading(true)
    setRefreshing(true)
    try {
      if (sync) {
        try {
          await smartoltService.runSyncNow(25)
        } catch {
          /* list cached data */
        }
      }
      const next = await smartoltService.getOltSettings()
      setData({ items: next?.items || [], summary: next?.summary || empty.summary, updated_at: next?.updated_at || null })
    } catch {
      toast.error("Failed to load OLT settings")
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const filtered = useMemo(
    () => filterRows(data.items, search, ["name", "olt_id", "ip", "hardware_version"]),
    [data.items, search],
  )

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">{title}</h1>
          <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-1.5 font-semibold text-emerald-700">
            {data.summary.reachable} reachable
          </span>
          {data.summary.stale > 0 ? (
            <span className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-1.5 font-semibold text-rose-700">
              {data.summary.stale} stale
            </span>
          ) : null}
        </div>
      </div>

      {/* Main Table Card */}
      <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-xs">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50/50 p-3.5">
          <div className="relative min-w-[200px] flex-1 sm:max-w-xs">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search OLTs…"
              className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-8 pr-3 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/20"
            />
          </div>
          <button
            type="button"
            disabled={refreshing}
            onClick={() => load(true, true)}
            className="ml-auto inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 shadow-xs transition-colors hover:bg-slate-50 hover:text-blue-600 disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Sync from SmartOLT
          </button>
        </div>

        {loading ? (
          <div className="px-4 py-12 text-center">
            <RefreshCw className="mx-auto h-7 w-7 animate-spin text-blue-500 mb-2" />
            <p className="text-slate-500 text-sm">Loading OLTs…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-14 text-center">
            <Server className="mx-auto h-12 w-12 text-slate-300 mb-3" />
            <p className="text-sm font-semibold text-slate-800">No OLTs synced yet</p>
            <p className="mt-1 text-xs text-slate-500 max-w-sm mx-auto">
              Configure SmartOLT tenant credentials in system config, then run sync to discover active OLTs.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  {["OLT Name", "IP Address", "Status", "ONUs (Online/Total)", "Uptime", "Synced"].map((h, i) => (
                    <th key={h} className={`px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-600 ${i === 3 ? "text-right" : "text-left"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((row) => (
                  <tr key={row.olt_id} className="transition-colors hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <Link to={SMARTOLT_PATHS.oltDetail(row.olt_id)} className="font-semibold text-blue-600 hover:text-blue-800 hover:underline">
                        {row.name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600">{row.ip || "—"}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center rounded-md px-2.5 py-0.5 text-[11px] font-semibold ${
                          row.reachable
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                            : "bg-rose-50 text-rose-700 border border-rose-200"
                        }`}
                      >
                        {row.reachable ? "Reachable" : "Stale"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-semibold text-emerald-700">{row.online}</span>
                      <span className="text-slate-400"> / </span>
                      <span className="font-semibold text-slate-700">{row.onus}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 font-mono text-xs">{row.uptime || "—"}</td>
                    <td className="px-4 py-3 text-slate-500 font-mono text-xs">{formatUpdated(row.fetched_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
